import {
  Activity,
  AlertTriangle,
  ArrowRight,
  Ban,
  Brain,
  FileText,
  Lock,
  Plug,
  Scale,
  Shield,
} from "lucide-react";
import { Link } from "react-router-dom";

const sections = [
  {
    num: "1",
    icon: Brain,
    title: "AI Across the Furistic Platform",
    content: `Furistic Technologies Inc. ("Furistic," "we," "us," or "our") integrates artificial intelligence throughout our platform and products. This AI Disclaimer applies to all AI-powered features across our products, including but not limited to:

**RealtyOS AI Features** — Property valuation estimates, market trend analyses, automated property descriptions, client matching algorithms, document analysis and extraction, comparative market analyses, and predictive analytics for real estate markets.

**TradeOS AI Features** — Trading signal generation, portfolio optimization suggestions, risk assessment models, market sentiment analysis, pattern recognition, price prediction models, and automated strategy backtesting.

**ConformOS AI Features** — Evidence classification, clause matching, gap analysis, and draft audit findings.

**Haulyard AI Features** — Route and dispatch assistance, document extraction, and operational summaries.

**Foodium AI Features** — Phone and chat ordering conversations, menu import assistance, and order summaries.

**Platform-Wide AI** — Natural language processing, intelligent search, automated data extraction, predictive analytics, and AI-assisted workflows, including Otto, the shared AI assistant.

All AI features are designed to augment human decision-making, not replace it. The outputs of our AI systems should be used as one input among many in your decision-making process.`,
  },
  {
    num: "2",
    icon: Ban,
    title: "No Professional Advice",
    content: `**Furistic does not provide professional advice of any kind.** The AI-generated outputs and information available through our Platform are for informational purposes only and should not be construed as:

• **Legal advice** — AI outputs do not constitute legal advice. Consult a qualified attorney for legal matters related to real estate transactions, trading regulations, or any other legal questions.

• **Financial advice** — Trading signals, portfolio suggestions, and market analyses generated by TradeOS are not financial advice. Consult a licensed financial advisor before making investment decisions. Past performance of AI models does not guarantee future results.

• **Real estate advice** — Property valuations, market analyses, and recommendations generated by RealtyOS are estimates only and do not constitute professional real estate appraisals. Consult a licensed appraiser or real estate professional for property valuations.

• **Tax advice** — Any tax-related calculations or information provided through the Platform are estimates only. Consult a qualified tax professional for tax-related decisions.

• **Investment advice** — No content on the Platform should be interpreted as a recommendation to buy, sell, or hold any financial instrument, property, or asset.

You acknowledge that decisions made based on AI outputs are made at your own risk and discretion.`,
  },
  {
    num: "3",
    icon: AlertTriangle,
    title: "AI Limitations & Accuracy",
    content: `While we strive for accuracy and continuously improve our AI systems, you should be aware of the following limitations:

**Hallucinations & Errors.** AI systems may generate outputs that appear plausible but are factually incorrect, incomplete, or misleading. This is an inherent limitation of current AI technology. Always verify AI-generated information against authoritative sources.

**Data Dependency.** AI outputs are only as good as the data they are trained on and the data they process. Incomplete, outdated, or inaccurate input data will produce unreliable outputs.

**Market Conditions.** AI models trained on historical data may not accurately predict outcomes in unprecedented market conditions, black swan events, or rapidly changing environments.

**Bias.** Despite our efforts to mitigate bias, AI systems may reflect biases present in training data. We actively monitor for and work to reduce bias in our models, but no AI system is completely free from bias.

**Context Limitations.** AI systems may not fully understand the nuances of your specific situation, local regulations, market conditions, or personal circumstances.

**No Guarantee of Outcomes.** Furistic makes no warranties or guarantees regarding the accuracy, completeness, reliability, or suitability of any AI-generated output for any particular purpose.`,
  },
  {
    num: "4",
    icon: Activity,
    title: "Service Availability",
    content: `Furistic aims to provide reliable, high-availability services but does not guarantee uninterrupted access to the Platform or any of its AI features.

• The Platform may experience downtime for maintenance, updates, or due to circumstances beyond our control
• AI features may be temporarily unavailable during model updates, retraining, or infrastructure changes
• Response times for AI features may vary based on system load, query complexity, and infrastructure conditions
• We reserve the right to modify, suspend, or discontinue any AI feature with reasonable notice
• Service level agreements (SLAs) for uptime and performance are specified in your subscription agreement, where applicable

You should not rely on the continuous availability of any AI feature for time-critical operations without having appropriate fallback procedures in place.`,
  },
  {
    num: "5",
    icon: Plug,
    title: "Third-Party Integrations",
    content: `The Platform integrates with third-party services, data providers, and APIs. Regarding these integrations:

• **Data Accuracy.** We do not guarantee the accuracy, completeness, or timeliness of data received from third-party sources (including MLS systems, market data feeds, financial data providers, and other external services).

• **Availability.** Third-party service outages or changes may affect Platform functionality. We are not responsible for the availability or performance of third-party services.

• **Terms of Use.** Your use of third-party services accessed through the Platform may be subject to additional terms and conditions imposed by those providers.

• **AI Processing of Third-Party Data.** When our AI processes data from third-party sources, the limitations described in this Disclaimer apply equally to those outputs. Third-party data passing through our AI models does not become more reliable by virtue of AI processing.`,
  },
  {
    num: "6",
    icon: Scale,
    title: "Regulatory Compliance",
    content: `AI-generated outputs from the Platform may be subject to industry-specific regulations. You are responsible for ensuring your use of AI outputs complies with all applicable laws and regulations, including but not limited to:

• Real estate licensing and disclosure requirements
• Securities regulations and trading compliance rules
• Fair housing laws and anti-discrimination requirements
• Consumer protection regulations
• Data protection and privacy laws
• Industry-specific codes of conduct and professional standards

Furistic does not monitor or ensure your compliance with industry-specific regulations. It is your responsibility to verify that your use of AI-generated outputs meets all applicable regulatory requirements.`,
  },
  {
    num: "7",
    icon: Shield,
    title: "Indemnification",
    content: `You agree to indemnify, defend, and hold harmless Furistic Technologies Inc. and its officers, directors, employees, agents, and affiliates from and against any claims, liabilities, damages, losses, costs, or expenses (including reasonable legal fees) arising from:

• Your reliance on AI-generated outputs from the Platform
• Your use of AI outputs in violation of applicable laws or regulations
• Your failure to verify AI-generated information before acting on it
• Any decisions or actions taken based on AI-generated outputs from the Platform
• Your representation of AI-generated content as human-generated professional advice

This indemnification obligation shall survive termination of your account and these Terms.`,
  },
];

export default function DisclaimerPage() {
  return (
    <main className="min-h-screen bg-[#09090b]">
      {/* Header */}
      <section className="relative pt-32 pb-16 bg-[#09090b]">
        <div className="absolute inset-0 bg-gradient-to-b from-violet-950/20 to-transparent" />
        <div className="container mx-auto px-6 relative">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-violet-500/10 border border-violet-500/20 text-violet-400 text-sm mb-6">
              <AlertTriangle className="w-4 h-4" />
              Legal
            </div>
            <h1 className="text-4xl md:text-5xl font-bold font-display text-white mb-4">
              AI Disclaimer
            </h1>
            <p className="text-lg text-zinc-400">
              Last Updated: March 18, 2026
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="pb-24">
        <div className="container mx-auto px-6 max-w-4xl">
          {/* Intro callout */}
          <div className="bg-white dark:bg-zinc-900/50 rounded-2xl p-8 md:p-12 border border-zinc-200 dark:border-zinc-800 mb-8">
            <div className="border-l-4 border-amber-500 pl-6">
              <p className="text-zinc-700 dark:text-zinc-300 leading-relaxed">
                <strong className="text-zinc-900 dark:text-white">
                  Important:
                </strong>{" "}
                Furistic products use artificial intelligence extensively.
                AI-generated outputs are not a substitute for professional
                advice. This Disclaimer explains the limitations of AI across
                all Furistic products and your responsibilities when using
                AI-powered features.
              </p>
            </div>
          </div>

          {/* Sections */}
          <div className="space-y-6">
            {sections.map((section) => {
              const Icon = section.icon;
              return (
                <div
                  key={section.num}
                  className="bg-white dark:bg-zinc-900/50 rounded-2xl p-8 md:p-12 border border-zinc-200 dark:border-zinc-800"
                >
                  <div className="flex items-start gap-4 mb-6">
                    <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-amber-500/10 border border-amber-500/20 flex items-center justify-center">
                      <span className="text-sm font-bold text-amber-400">
                        {section.num}
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Icon className="w-5 h-5 text-amber-400" />
                      <h2 className="text-xl md:text-2xl font-bold text-zinc-900 dark:text-white">
                        {section.title}
                      </h2>
                    </div>
                  </div>
                  <div className="text-zinc-700 dark:text-zinc-300 leading-relaxed whitespace-pre-line pl-14">
                    {section.content}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Contact */}
          <div className="bg-white dark:bg-zinc-900/50 rounded-2xl p-8 md:p-12 border border-zinc-200 dark:border-zinc-800 mt-8">
            <h2 className="text-xl font-bold text-zinc-900 dark:text-white mb-4">
              Questions About Our AI?
            </h2>
            <p className="text-zinc-700 dark:text-zinc-300 leading-relaxed">
              If you have questions about AI usage across our products, please
              contact us at{" "}
              <a
                href="mailto:legal@furistic.co"
                className="text-violet-400 hover:text-violet-300 underline"
              >
                legal@furistic.co
              </a>
              .
            </p>
          </div>

          {/* Other Legal Pages */}
          <div className="mt-12 pt-8 border-t border-zinc-800">
            <h3 className="text-sm font-semibold text-zinc-500 uppercase tracking-wider mb-4">
              Other Legal Pages
            </h3>
            <div className="grid sm:grid-cols-3 gap-4">
              {[
                {
                  href: "/legal/terms",
                  label: "Terms of Service",
                  icon: FileText,
                },
                {
                  href: "/legal/privacy",
                  label: "Privacy Policy",
                  icon: Shield,
                },
                {
                  href: "/legal/security",
                  label: "Security & Infrastructure",
                  icon: Lock,
                },
              ].map((link) => {
                const LIcon = link.icon;
                return (
                  <Link
                    key={link.href}
                    to={link.href}
                    className="flex items-center gap-3 p-4 rounded-xl bg-zinc-900/50 border border-zinc-800 hover:border-violet-500/30 transition group"
                  >
                    <LIcon className="w-4 h-4 text-violet-400" />
                    <span className="text-sm text-zinc-300 group-hover:text-white transition">
                      {link.label}
                    </span>
                    <ArrowRight className="w-4 h-4 text-zinc-600 group-hover:text-violet-400 ml-auto transition" />
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
